"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Instagram, Facebook, Sprout, ArrowUp } from "lucide-react";
const TikTokIcon = (props: React.SVGProps<SVGSVGElement>) => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>;
const YouTubeIcon = (props: React.SVGProps<SVGSVGElement>) => <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>;
export default function Footer() {
  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };
  const quickLinks = [{
    name: "Home",
    href: "/"
  }, {
    name: "Features",
    href: "/featurespage/"
  }, {
    name: "Partners",
    href: "/partners/"
  }, {
    name: "Hay Planner",
    href: "https://thebarndesk.com/hay-planner/",
    newTab: true
  }, {
    name: "User Guide",
    href: "https://thebarndesk.com/user-guide/"
  }, {
    name: "Download",
    href: "/download/"
  }, {
    name: "Log In",
    href: "https://app.thebarndesk.com"
  }];
  const socials = [{
    name: "Instagram",
    icon: Instagram,
    href: "https://www.instagram.com/barndesk/"
  }, {
    name: "Facebook",
    icon: Facebook,
    href: "https://www.facebook.com/profile.php?id=61589368375810"
  }, {
    name: "TikTok",
    icon: TikTokIcon,
    href: "https://www.tiktok.com/@barndesk"
  }, {
    name: "YouTube",
    icon: YouTubeIcon,
    href: "https://www.youtube.com/@TheBarnDesk"
  }, {
    name: "Blackey Farms",
    icon: Sprout,
    href: "https://blackeyfarms.com"
  }];
  const legal = [{
    name: "Terms of Service",
    href: "https://thebarndesk.com/terms-of-service/"
  }, {
    name: "Privacy Policy",
    href: "https://thebarndesk.com/privacy-policy/"
  }];
  return <section data-section-id="254000" id="footer" className="bg-background text-foreground/80 relative overflow-hidden py-24 px-6 md:px-12 font-sans text-xs border-t border-border/30">
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none select-none overflow-hidden z-0">
        <WvcLogo className="w-[150vw] md:w-[100vw] h-auto max-w-none text-primary" />
      </div>
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8 mb-24">
          <div className="flex flex-col">
            <h3 className="font-default text-sm uppercase tracking-widest mb-8 text-primary font-bold">Quick Links</h3>
            <ul className="flex flex-col gap-4">
              {quickLinks.map((item, i) => <li key={item.name} data-index={i}>
                  <Link to={item.href} newTab={item.newTab} className="hover:text-primary transition-colors duration-300 font-default text-sm">
                    {item.name}
                  </Link>
                </li>)}
            </ul>
          </div>
          <div className="flex flex-col">
            <h3 className="font-default text-sm uppercase tracking-widest mb-8 text-primary font-bold">Community</h3>
            <ul className="flex flex-col gap-4">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <li key={social.name} data-index={i}>
                    <Link to={social.href} className="flex items-center gap-3 hover:text-primary transition-colors duration-300 group font-default text-sm">
                      <Icon className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-300" strokeWidth={1.5} />
                      <span>{social.name}</span>
                    </Link>
                  </li>;
            })}
            </ul>
          </div>
          <div className="flex flex-col">
            <h3 className="font-default text-sm uppercase tracking-widest mb-8 text-primary font-bold">Legal</h3>
            <ul className="flex flex-col gap-4">
              {legal.map((item, i) => <li key={item.name} data-index={i}>
                  <Link to={item.href} className="hover:text-primary transition-colors duration-300 font-default text-sm">
                    {item.name}
                  </Link>
                </li>)}
            </ul>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-8 pt-8 border-t border-border/30">
          <div className="flex items-center gap-6">
            <Link to="/">
              <WvcLogo className="h-8 w-auto" />
            </Link>
            <div className="flex flex-col">
              <span className="text-foreground/80 font-serif italic text-sm">Built for real farm life.</span>
              <span className="text-muted-foreground text-[10px]">© 2026 BarnDesk LLC. Proudly independent.</span>
            </div>
          </div>
          <Button variant="ghost" onClick={scrollToTop} className="group flex items-center gap-2 hover:bg-accent/50 hover:text-primary px-4 rounded-lg transition-all duration-300">
            <span className="uppercase tracking-widest font-default text-[10px] font-semibold">Back to top</span>
            <ArrowUp className="w-4 h-4 text-primary group-hover:-translate-y-1 transition-transform duration-300" />
          </Button>
        </div>
      </div>
    </section>;
}